import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';
import { supabase } from '../lib/supabase';
import { APPLICATION_COLUMNS, fetchSensitive, statusLabel } from '../lib/applications';
import './ApplicationPrint.css';

// A clean, printable single application for school files and interviews. Medical and special-needs answers are left
// out unless an admin ticks the box; including them is recorded in the audit log. Internal notes are never printed.
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const Row = ({ label, children }) => (
  <div className="print-row">
    <dt>{label}</dt>
    <dd>{children || '—'}</dd>
  </div>
);

const ApplicationPrint = () => {
  const { id } = useParams();
  const { user, isViewer } = useAuth();
  const { settings, gradeLabel } = useSchool();
  const [app, setApp] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [sensitive, setSensitive] = useState(null);
  const [sensitiveError, setSensitiveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [a, h] = await Promise.all([
        supabase.from('admissions').select(APPLICATION_COLUMNS).eq('id', id).single(),
        supabase
          .from('status_history')
          .select('*, changed_by_user:admin_users(full_name)')
          .eq('application_id', id)
          .order('created_at', { ascending: true })
      ]);
      if (cancelled) return;
      if (a.error || !a.data) {
        setError('Application not found');
        return;
      }
      setApp(a.data);
      setHistory(h.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const toggleSensitive = async (checked) => {
    setIncludeSensitive(checked);
    setSensitiveError('');
    if (checked && !sensitive) {
      try {
        const rows = await fetchSensitive([app.id], 'print');
        setSensitive(rows[app.id] ?? { medical_conditions: null, special_needs: null });
      } catch (e) {
        console.error('Error loading medical information:', e);
        setSensitiveError('Medical information could not be loaded.');
        setIncludeSensitive(false);
      }
    }
  };

  if (error) {
    return (
      <div className="print-page">
        <p role="alert">{error}</p>
        <Link to="/admin/applications">Back to applications</Link>
      </div>
    );
  }
  if (!app) return <div className="print-page">Loading...</div>;

  return (
    <div className="print-page">
      <div className="print-toolbar no-print">
        <Link to={`/admin/applications/${app.id}`}>Back to the application</Link>
        {!isViewer && (
          <label>
            <input type="checkbox" checked={includeSensitive} onChange={(e) => toggleSensitive(e.target.checked)} />
            Include medical and special-needs answers
          </label>
        )}
        <button type="button" onClick={() => window.print()}>Print</button>
        {sensitiveError && <span role="alert">{sensitiveError}</span>}
      </div>

      <header className="print-header">
        <div>
          <p className="print-school">{settings.school_name}</p>
          <h1>Admission application {app.reference_number}</h1>
        </div>
        <dl className="print-summary">
          <Row label="Status">{statusLabel(app.status)}</Row>
          <Row label="Submitted">{fmtDateTime(app.created_at)}</Row>
          <Row label="Academic year">{app.academic_year}</Row>
          <Row label="Interview / test">{fmtDateTime(app.interview_at)}</Row>
        </dl>
      </header>

      <section>
        <h2>Student</h2>
        <dl>
          <Row label="Name">{`${app.student_first_name} ${app.student_last_name}`}</Row>
          <Row label="Date of birth">{fmtDate(app.date_of_birth)}</Row>
          <Row label="Gender">{app.gender}</Row>
          <Row label="Grade/Program">{gradeLabel(app.grade)}</Row>
          <Row label="Previous school">{app.previous_school}</Row>
        </dl>
      </section>

      <section>
        <h2>Parent / guardian</h2>
        <dl>
          <Row label="Name">{`${app.parent_first_name} ${app.parent_last_name}`}</Row>
          <Row label="Email">{app.email}</Row>
          <Row label="Phone">{app.phone}</Row>
          <Row label="Address">{[app.address, app.city, app.state, app.zip_code].filter(Boolean).join(', ')}</Row>
        </dl>
      </section>

      <section>
        <h2>Emergency contact</h2>
        <dl>
          <Row label="Name">{app.emergency_contact_name}</Row>
          <Row label="Phone">{app.emergency_contact_phone}</Row>
          <Row label="Relationship">{app.emergency_contact_relation}</Row>
        </dl>
      </section>

      <section>
        <h2>Additional information</h2>
        <dl>
          <Row label="How they heard about us">{app.how_did_you_hear}</Row>
          <Row label="Comments">{app.comments}</Row>
        </dl>
      </section>

      {includeSensitive && sensitive && (
        <section className="print-sensitive">
          <h2>Medical and special needs (confidential)</h2>
          <dl>
            <Row label="Medical conditions">{sensitive.medical_conditions}</Row>
            <Row label="Special needs">{sensitive.special_needs}</Row>
          </dl>
        </section>
      )}

      <section>
        <h2>History</h2>
        <table className="print-history">
          <thead>
            <tr><th>Date</th><th>Change</th><th>By</th><th>Notes</th></tr>
          </thead>
          <tbody>
            <tr><td>{fmtDateTime(app.created_at)}</td><td>Application submitted</td><td></td><td></td></tr>
            {history.map((h) => (
              <tr key={h.id}>
                <td>{fmtDateTime(h.created_at)}</td>
                <td>{statusLabel(h.old_status)} → {statusLabel(h.new_status)}</td>
                <td>{h.changed_by_user?.full_name || ''}</td>
                <td>{h.notes || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <footer className="print-footer">
        Printed {new Date().toLocaleString()} by {user?.full_name || user?.username}. Contains personal data about a
        child: store securely and destroy when no longer needed.
      </footer>
    </div>
  );
};

export default ApplicationPrint;
